/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React from "react";
import "./AdminDSR.css";
import { AiOutlineClose } from "react-icons/ai";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import * as XLSX from "xlsx";

const parseTimeToMinutes = (timeStr) => {
  if (timeStr === null || timeStr === undefined || timeStr === "") return 0;
  if (typeof timeStr === "number") {
    return Math.round(timeStr * 60);
  }

  const str = String(timeStr).trim();

  const hMatch = str.match(/(\d+(?:\.\d+)?)\s*h/i);
  const mMatch = str.match(/(\d+)\s*m/i);
  if (hMatch || mMatch) {
    const h = hMatch ? parseFloat(hMatch[1]) : 0;
    const m = mMatch ? parseInt(mMatch[1], 10) : 0;
    return Math.round(h * 60 + m);
  }

  if (str.includes(":")) {
    const parts = str.split(":").map((p) => parseInt(p, 10));
    if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return parts[0] * 60 + parts[1];
    }
  }

  const num = parseFloat(str);
  if (!isNaN(num)) {
    return Math.round(num * 60);
  }

  return 0;
};

const formatMinutesToTime = (totalMins) => {
  const mins = Math.max(0, Math.round(totalMins));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h} h ${m} m`;
};

const groupDataByProject = (data) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const groups = {};
  data.forEach((item) => {
    const pName = item.ProjectName || "Unknown Project";
    if (!groups[pName]) {
      groups[pName] = {
        projectName: pName,
        records: [],
        totalMinutes: 0,
      };
    }
    groups[pName].records.push(item);
    groups[pName].totalMinutes += parseTimeToMinutes(item.TotalTime);
  });

  return Object.values(groups).map((group) => {
    const uniqueEmployees = [
      ...new Set(group.records.map((r) => r.UserName).filter(Boolean)),
    ];
    return {
      ...group,
      totalTime: formatMinutesToTime(group.totalMinutes),
      employeeNames: uniqueEmployees.join(", "),
    };
  });
};

const MonthlyReportTab = ({
  // filters
  reportFilters,
  setReportFilters,
  reportProjects,
  reportEmployees,
  fetchReportEmployees,
  fetchReportProjects,
  fetchMonthlyReport,
  handleReportReset,

  // table data
  monthlyReportData,
  reportTotalTime,
  fetchTasksForAdmin,

  // task modal
  showTaskModal,
  setShowTaskModal,
  taskModalData,
  filteredTaskList,
  taskSearch,
  setTaskSearch,
  taskList,
  setFilteredTaskList,
}) => {
  const handleDownloadExcel = () => {
    if (!monthlyReportData || monthlyReportData.length === 0) return;

    const grouped = groupDataByProject(monthlyReportData);
    const rows = [];

    grouped.forEach((group, gIdx) => {
      rows.push({
        "#": `Project ${gIdx + 1}`,
        "Project / Employee Name": group.projectName,
        "Time": `Total: ${group.totalTime}`,
      });

      group.records.forEach((item, index) => {
        rows.push({
          "#": index + 1,
          "Project / Employee Name": item.UserName,
          "Time": item.TotalTime,
        });
      });

      rows.push({
        "#": "",
        "Project / Employee Name": "",
        "Time": "",
      });
    });

    if (reportTotalTime) {
      rows.push({
        "#": "",
        "Project / Employee Name": "Overall Total Time",
        "Time": reportTotalTime,
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);

    worksheet["!cols"] = [
      { wch: 14 },
      { wch: 30 },
      { wch: 18 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Report");

    const fromDate = reportFilters.fromDate || "";
    const toDate = reportFilters.toDate || "";
    const fileName = `Monthly_Report_${fromDate}_to_${toDate}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  const handleDownloadTaskExcel = () => {
    if (!filteredTaskList || filteredTaskList.length === 0) return;

    const infoRows = [
      ["Project Name", taskModalData?.projectName || ""],
      ["Employee Name", taskModalData?.employeeName || ""],
      ["From Date", reportFilters.fromDate || ""],
      ["To Date", reportFilters.toDate || ""],
      [],
      ["#", "Date", "Task", "Time"],
    ];

    const taskRows = filteredTaskList.map((task, idx) => [
      idx + 1,
      task.date,
      task.task,
      task.time,
    ]);

    let totalRow = [];
    if (taskModalData?.totalTime) {
      const [h, m] = taskModalData.totalTime.split(":");
      const formattedTotal = `${parseInt(h, 10)} h ${parseInt(m, 10)} m`;
      totalRow = [
        [],
        ["", "", "Total Time", formattedTotal],
      ];
    }

    const worksheet = XLSX.utils.aoa_to_sheet([...infoRows, ...taskRows, ...totalRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Task Details");

    const cleanProject = (taskModalData?.projectName || "Project")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    const cleanEmployee = (taskModalData?.employeeName || "Employee")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    const fileName = `task_detail_${cleanProject}_${cleanEmployee}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  const handleDownloadFullScreen = () => {
    if (!monthlyReportData || monthlyReportData.length === 0) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const fromDate = reportFilters.fromDate || "N/A";
    const toDate = reportFilters.toDate || "N/A";
    const grouped = groupDataByProject(monthlyReportData);

    const projectTablesHtml = grouped
      .map(
        (group) => `
        <div style="margin-bottom: 20px;">
          <div style="background-color: #f1f5f9; padding: 8px 12px; border: 1px solid #cbd5e1; border-bottom: none; display: flex; justify-content: space-between; font-weight: bold; color: #0066cc;">
            <span>Project: ${group.projectName}</span>
            <span>Total Time: ${group.totalTime}</span>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <thead>
              <tr>
                <th style="text-align: center; width: 50px;">#</th>
                <th>Employee Name</th>
                <th style="text-align: center; width: 100px;">Time</th>
              </tr>
            </thead>
            <tbody>
              ${group.records
                .map(
                  (item, idx) => `
                <tr>
                  <td style="text-align: center; width: 50px;">${idx + 1}</td>
                  <td>${item.UserName || "-"}</td>
                  <td style="text-align: center; width: 100px;">${item.TotalTime || "-"}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </div>
      `
      )
      .join("");

    const totalTimeHtml = reportTotalTime
      ? `<div style="margin-top: 10px; font-size: 14px; font-weight: bold; text-align: right;">Overall Total Time: ${reportTotalTime}</div>`
      : "";

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Monthly DSR Report</title>
        <style>
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
            font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          }
          body {
            padding: 10px 16px;
            background-color: #fff;
            color: #333;
          }
          .header-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
            padding-bottom: 6px;
            border-bottom: 2px solid #0066cc;
          }
          .title-section h1 {
            font-size: 18px;
            color: #0066cc;
            margin-bottom: 2px;
          }
          .title-section p {
            font-size: 12px;
            color: #666;
          }
          .actions-bar {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .density-label {
            font-size: 12px;
            font-weight: 600;
            color: #444;
          }
          .btn {
            padding: 4px 10px;
            font-size: 12px;
            font-weight: 600;
            border-radius: 4px;
            border: 1px solid #ccc;
            cursor: pointer;
            background-color: #f8fafc;
            transition: all 0.2s;
          }
          .btn:hover {
            background-color: #e2e8f0;
          }
          .btn-print {
            background-color: #0066cc;
            color: white;
            border: none;
          }
          .btn-print:hover {
            background-color: #004999;
          }
          .btn-close {
            background-color: #6c757d;
            color: white;
            border: none;
          }
          .btn-close:hover {
            background-color: #5a6268;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 0;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #cbd5e1;
            padding: 4px 8px;
            text-align: left;
            line-height: 1.25;
          }
          th {
            background-color: #f8fafc;
            color: #1e293b;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 11px;
            letter-spacing: 0.3px;
          }
          tr:nth-child(even) {
            background-color: #f8fafc;
          }
          tr:hover {
            background-color: #e2e8f0;
          }
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              padding: 0;
            }
            table {
              border: 1px solid #000;
            }
            th, td {
              border: 1px solid #000;
              padding: 3px 6px;
            }
          }
        </style>
        <script>
          function changeDensity(padding, fontSize) {
            const cells = document.querySelectorAll('th, td');
            cells.forEach(c => {
              c.style.padding = padding;
              if (fontSize) c.style.fontSize = fontSize;
            });
          }
        </script>
      </head>
      <body>
        <div class="header-container">
          <div class="title-section">
            <h1>Monthly DSR Report</h1>
            <p>Period: <strong>${fromDate}</strong> to <strong>${toDate}</strong> | Total Records: <strong>${monthlyReportData.length}</strong></p>
          </div>
          <div class="actions-bar no-print">
            <span class="density-label">View Density:</span>
            <button class="btn" onclick="changeDensity('2px 5px', '11px')">Ultra Compact (50+ rows)</button>
            <button class="btn" onclick="changeDensity('4px 8px', '12px')">Compact</button>
            <button class="btn" onclick="changeDensity('8px 12px', '13px')">Standard</button>
            <button class="btn btn-print" onclick="window.print()">Print / Save PDF</button>
            <button class="btn btn-close" onclick="window.close()">Close</button>
          </div>
        </div>

        ${projectTablesHtml}

        ${totalTimeHtml}
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleDownloadTaskFullScreen = () => {
    if (!filteredTaskList || filteredTaskList.length === 0) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const projectName = taskModalData?.projectName || "-";
    const employeeName = taskModalData?.employeeName || "-";
    const fromDate = reportFilters.fromDate || "N/A";
    const toDate = reportFilters.toDate || "N/A";

    const taskRowsHtml = filteredTaskList
      .map(
        (task, idx) => `
        <tr>
          <td style="text-align: center; width: 50px;">${idx + 1}</td>
          <td style="text-align: center; width: 110px;">${task.date || "-"}</td>
          <td>${task.task || "-"}</td>
          <td style="text-align: center; width: 90px;">${task.time || "-"}</td>
        </tr>
      `
      )
      .join("");

    let formattedTotalTime = "";
    if (taskModalData?.totalTime) {
      const [h, m] = taskModalData.totalTime.split(":");
      formattedTotalTime = `${parseInt(h, 10)} h ${parseInt(m, 10)} m`;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Task Details - ${employeeName}</title>
        <style>
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
            font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          }
          body {
            padding: 10px 16px;
            background-color: #fff;
            color: #333;
          }
          .header-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
            padding-bottom: 6px;
            border-bottom: 2px solid #0066cc;
          }
          .title-section h1 {
            font-size: 18px;
            color: #0066cc;
            margin-bottom: 2px;
          }
          .meta-info {
            display: flex;
            gap: 16px;
            margin-top: 4px;
            font-size: 12px;
            color: #475569;
          }
          .actions-bar {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .density-label {
            font-size: 12px;
            font-weight: 600;
            color: #444;
          }
          .btn {
            padding: 4px 10px;
            font-size: 12px;
            font-weight: 600;
            border-radius: 4px;
            border: 1px solid #ccc;
            cursor: pointer;
            background-color: #f8fafc;
            transition: all 0.2s;
          }
          .btn:hover {
            background-color: #e2e8f0;
          }
          .btn-print {
            background-color: #0066cc;
            color: white;
            border: none;
          }
          .btn-print:hover {
            background-color: #004999;
          }
          .btn-close {
            background-color: #6c757d;
            color: white;
            border: none;
          }
          .btn-close:hover {
            background-color: #5a6268;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 6px;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #dcdcdc;
            padding: 4px 8px;
            text-align: left;
            line-height: 1.25;
          }
          th {
            background-color: #f1f5f9;
            color: #1e293b;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 11px;
            letter-spacing: 0.3px;
          }
          tr:nth-child(even) {
            background-color: #f8fafc;
          }
          tr:hover {
            background-color: #e2e8f0;
          }
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              padding: 0;
            }
            table {
              border: 1px solid #000;
            }
            th, td {
              border: 1px solid #000;
              padding: 3px 6px;
            }
          }
        </style>
        <script>
          function changeDensity(padding, fontSize) {
            const cells = document.querySelectorAll('th, td');
            cells.forEach(c => {
              c.style.padding = padding;
              if (fontSize) c.style.fontSize = fontSize;
            });
          }
        </script>
      </head>
      <body>
        <div class="header-container">
          <div class="title-section">
            <h1>Task Details Report</h1>
            <div class="meta-info">
              <span><strong>Project:</strong> ${projectName}</span>
              <span><strong>Employee:</strong> ${employeeName}</span>
              <span><strong>Period:</strong> ${fromDate} to ${toDate}</span>
              <span><strong>Total Tasks:</strong> ${filteredTaskList.length}</span>
            </div>
          </div>
          <div class="actions-bar no-print">
            <span class="density-label">View Density:</span>
            <button class="btn" onclick="changeDensity('2px 5px', '11px')">Ultra Compact (50+ rows)</button>
            <button class="btn" onclick="changeDensity('4px 8px', '12px')">Compact</button>
            <button class="btn" onclick="changeDensity('8px 12px', '13px')">Standard</button>
            <button class="btn btn-print" onclick="window.print()">Print / Save PDF</button>
            <button class="btn btn-close" onclick="window.close()">Close</button>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="text-align: center; width: 50px;">#</th>
              <th style="text-align: center; width: 110px;">Date</th>
              <th>Task</th>
              <th style="text-align: center; width: 90px;">Time</th>
            </tr>
          </thead>
          <tbody>
            ${taskRowsHtml}
          </tbody>
        </table>

        ${
          formattedTotalTime
            ? `<div style="margin-top: 10px; font-size: 14px; font-weight: bold; text-align: right;">Total Time: ${formattedTotalTime}</div>`
            : ""
        }
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const [openProjects, setOpenProjects] = React.useState({});

  const toggleProjectCollapse = (projectName) => {
    setOpenProjects((prev) => ({
      ...prev,
      [projectName]: !prev[projectName],
    }));
  };

  const isDateOnlySearch =
    (!reportFilters.projectId || reportFilters.projectId === "0") &&
    (!reportFilters.userId || reportFilters.userId === "0");

  const groupedReportData = groupDataByProject(monthlyReportData);

  const getFormattedOverallTotalTime = () => {
    if (!monthlyReportData || monthlyReportData.length === 0) return "N/A";

    let totalMins = 0;
    monthlyReportData.forEach((item) => {
      totalMins += parseTimeToMinutes(item.TotalTime);
    });

    if (totalMins > 0) {
      return formatMinutesToTime(totalMins);
    }

    if (reportTotalTime) {
      const parsed = parseTimeToMinutes(reportTotalTime);
      return parsed > 0 ? formatMinutesToTime(parsed) : reportTotalTime;
    }

    return "N/A";
  };

  return (
    <>
      <div className="filters-section-AdminDSR">
        <div className="filter-group-AdminDSR">
          <label>From Date</label>
          <input
            type="date"
            className="date-input-AdminDSR"
            value={reportFilters.fromDate}
            onChange={(e) =>
              setReportFilters({
                ...reportFilters,
                fromDate: e.target.value,
                toDate: "",
              })
            }
          />
        </div>

        <div className="filter-group-AdminDSR">
          <label>To Date</label>
          <input
            type="date"
            className="date-input-AdminDSR"
            value={reportFilters.toDate}
            disabled={!reportFilters.fromDate}
            min={reportFilters.fromDate}
            onChange={(e) =>
              setReportFilters({
                ...reportFilters,
                toDate: e.target.value,
              })
            }
          />
        </div>

        <div className="filter-group-AdminDSR">
          <label>Select Project</label>
          <select
            className="select-dropdown-AdminDSR"
            value={reportFilters.projectId}
            onChange={(e) => {
              const projectId = e.target.value;
              // FIX: Only update projectId, keep userId intact.
              // Refetch employees filtered by this project but preserve current userId selection.
              setReportFilters((prev) => ({ ...prev, projectId }));
              fetchReportEmployees(projectId);
            }}
          >
            <option value="">Select</option>
            {reportProjects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group-AdminDSR">
          <label>Employee</label>
          <select
            className="select-dropdown-AdminDSR"
            value={reportFilters.userId}
            onChange={(e) => {
              const userId = e.target.value;
              // FIX: Only update userId, keep projectId intact.
              // Refetch projects filtered by this employee but preserve current projectId selection.
              setReportFilters((prev) => ({ ...prev, userId }));
              fetchReportProjects(userId);
            }}
          >
            <option value="">Select</option>
            {reportEmployees.map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="search-button-AdminDSR"
            onClick={fetchMonthlyReport}
          >
            Search
          </button>

          <button
            className="cancel-button-AdminDSR"
            onClick={handleReportReset}
          >
            Reset
          </button>

          <button
            className="search-button-AdminDSR"
            onClick={handleDownloadExcel}
            disabled={!monthlyReportData || monthlyReportData.length === 0}
          >
            Download Excel
          </button>

          <button
            className="search-button-AdminDSR"
            onClick={handleDownloadFullScreen}
            disabled={!monthlyReportData || monthlyReportData.length === 0}
          >
            Download Full Screen
          </button>
        </div>
      </div>

      <div className="table-in-all-emplyee-AdminDSR">
        {isDateOnlySearch ? (
          groupedReportData.length > 0 ? (
            groupedReportData.map((projectGroup, pIdx) => {
              const isOpen = Boolean(openProjects[projectGroup.projectName]);
              return (
                <div className="project-report-card-AdminDSR" key={pIdx}>
                  <div
                    className="project-report-header-AdminDSR"
                    onClick={() => toggleProjectCollapse(projectGroup.projectName)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      {isOpen ? (
                        <FiChevronDown style={{ fontSize: "18px", color: "#0066cc", flexShrink: 0 }} />
                      ) : (
                        <FiChevronRight style={{ fontSize: "18px", color: "#0066cc", flexShrink: 0 }} />
                      )}
                      <h4 className="project-report-title-AdminDSR">
                        {projectGroup.projectName}
                      </h4>
                      {projectGroup.employeeNames && (
                        <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>
                          ({projectGroup.employeeNames})
                        </span>
                      )}
                    </div>
                    <span className="project-report-time-AdminDSR">
                      Total Time: {projectGroup.totalTime}
                    </span>
                  </div>
                  {isOpen && (
                    <table className="table-main-AdminDSR">
                      <thead className="thead-main-AdminDSR">
                        <tr>
                          <th>#</th>
                          <th>Employee Name</th>
                          <th>Time</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody className="tbody-main-AdminDSR">
                        {projectGroup.records.map((item, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{item.UserName}</td>
                            <td>{item.TotalTime}</td>
                            <td>
                              <button
                                className="view-button-AdminDSR"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchTasksForAdmin(item);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })
          ) : (
            <table className="table-main-AdminDSR">
              <thead className="thead-main-AdminDSR">
                <tr>
                  <th>#</th>
                  <th>Employee Name</th>
                  <th>Time</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody className="tbody-main-AdminDSR">
                <tr>
                  <td colSpan="4" style={{ textAlign: "center" }}>No record found</td>
                </tr>
              </tbody>
            </table>
          )
        ) : (
          <table className="table-main-AdminDSR">
            <thead className="thead-main-AdminDSR">
              <tr>
                <th>#</th>
                <th>Project Name</th>
                <th>Employee Name</th>
                <th>Time</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody className="tbody-main-AdminDSR">
              {monthlyReportData.length > 0 ? (
                monthlyReportData.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.ProjectName}</td>
                    <td>{item.UserName}</td>
                    <td>{item.TotalTime}</td>
                    <td>
                      <button
                        className="view-button-AdminDSR"
                        onClick={() => fetchTasksForAdmin(item)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>No record found</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {monthlyReportData.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
              marginBottom: "15px",
            }}
          >
            <div
              style={{
                fontWeight: "700",
                fontSize: "16px",
                color: "#0066cc",
                background: "#f0f7ff",
                padding: "10px 24px",
                borderRadius: "6px",
                border: "1px solid #bae0ff",
                boxShadow: "0 1px 3px rgba(0, 102, 204, 0.1)",
              }}
            >
              Total Time: {getFormattedOverallTotalTime()}
            </div>
          </div>
        )}
      </div>

      {showTaskModal && (
        <div className="modal-overlay-AdminDSR">
          <div className="modal-content-task-AdminDSR">
            <div className="modal-header-AdminDSR">
              <h3>Task Details</h3>
              <button
                onClick={() => setShowTaskModal(false)}
                className="close-button-AdminDSR"
              >
                <AiOutlineClose />
              </button>
            </div>

            <div className="modal-body-task-AdminDSR">
              <div className="task-info-row-AdminDSR">
                <div className="task-info-field-AdminDSR">
                  <label>Project Name</label>
                  <input
                    type="text"
                    value={taskModalData?.projectName || ""}
                    readOnly
                  />
                </div>
                <div className="task-info-field-AdminDSR">
                  <label>Employee Name</label>
                  <input
                    type="text"
                    value={taskModalData?.employeeName || ""}
                    readOnly
                  />
                </div>
              </div>

              <div className="task-info-row-AdminDSR">
                <div className="task-info-field-AdminDSR">
                  <label>From Date</label>
                  <input type="text" value={reportFilters.fromDate} readOnly />
                </div>
                <div className="task-info-field-AdminDSR">
                  <label>To Date</label>
                  <input type="text" value={reportFilters.toDate} readOnly />
                </div>
              </div>

              <div className="task-entries-AdminDSR">
                <div className="search-in-task-AdminDSR">
                  <label>Search:</label>
                  <input
                    type="text"
                    value={taskSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTaskSearch(value);

                      const filtered = taskList.filter((task) =>
                        task.task.toLowerCase().includes(value.toLowerCase())
                      );

                      setFilteredTaskList(filtered);
                    }}
                  />
                </div>

                <button
                  className="search-button-AdminDSR"
                  onClick={handleDownloadTaskExcel}
                  disabled={!filteredTaskList || filteredTaskList.length === 0}
                >
                  Download Excel
                </button>

                <button
                  className="search-button-AdminDSR"
                  onClick={handleDownloadTaskFullScreen}
                  disabled={!filteredTaskList || filteredTaskList.length === 0}
                  style={{ marginLeft: "10px" }}
                >
                  Download Full Screen
                </button>
              </div>

              <div className="table-in-all-emplyee-AdminDSR">
                <table className="table-main-AdminDSR">
                  <thead className="thead-main-AdminDSR">
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Task</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody className="tbody-main-AdminDSR">
                    {filteredTaskList.length > 0 ? (
                      filteredTaskList.map((task, idx) => (
                        <tr key={task.id}>
                          <td>{idx + 1}</td>
                          <td>{task.date}</td>
                          <td>{task.task}</td>
                          <td>{task.time}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4">No tasks found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {taskModalData?.totalTime && (
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: "16px",
                    marginTop: "15px",
                  }}
                >
                  Total Time: {(() => {
                    const [h, m] = taskModalData.totalTime.split(":");
                    return `${parseInt(h, 10)} h ${parseInt(m, 10)} m`;
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MonthlyReportTab;